import React from 'react';
import PropTypes from 'prop-types';
import { View, StyleSheet } from 'react-native';
import { Card, Icon, Text } from '@ui-kitten/components';
import lodashFind from 'lodash/find';

import { pluralize } from '../../Helper';
import useCart from '../../Hooks/useCart';
import useStorePersist from '../../Hooks/useStorePersist';
import ThemeColor from '../../Theme/colors';
import ThemeStyle from '../../Theme/styles';

import Button from '../Button';
import constants from '../../Config/constants';
import ScreenSize from '../../Helper/ScreenSize';

const textCategory = 'p2';

function LimitReached({
  maxLimit,
  productId,
  selectorValue,
  stocks,
  storeId,
  onReschedule,
}) {
  const { xs } = ScreenSize();
  const { getSavedStore } = useStorePersist();
  const { isCanReschedule } = useCart();

  const storeData = getSavedStore(storeId);
  const showReschedule = isCanReschedule(storeId, productId, selectorValue);
  const productData = lodashFind(
    storeData?.menu,
    (menu) => menu.item.id === productId,
  );
  const itemCategoryData = lodashFind(storeData?.categories, {
    category_name: stocks?.categoryName,
  });
  const remainingNote = `You can only add ${maxLimit} ${pluralize(
    maxLimit,
    'piece',
  )} of this`;
  const categoryOrItem = stocks?.isCategoryStock ? 'category' : 'item';
  const categoryLimitReached =
    itemCategoryData?.category_limit_per_day > 0 ? 'today' : 'this time slot';
  const itemLimitReached =
    productData?.item_limit_per_day > 0 ? 'today' : 'this time slot';
  const dailyOrTimeslot = stocks?.isCategoryStock
    ? categoryLimitReached
    : itemLimitReached;

  const nonMealPlanMsg = `${remainingNote} ${categoryOrItem} for ${dailyOrTimeslot}.`;
  const btnTxt = 'Do you want to change your order schedule?';

  return (
    <View style={constants.isWeb ? styles.webWrapper : styles.mobileWrapper}>
      <Card style={{ backgroundColor: ThemeColor.gray400 }}>
        <View style={ThemeStyle.flexDirectionRowCenterCenter}>
          <Icon
            name="info-outline"
            fill={ThemeColor.darkGray}
            style={[{ width: 30, height: 30 }, ThemeStyle.spacingRight]}
          />
          <Text category={textCategory} style={ThemeStyle.flex1}>
            {nonMealPlanMsg}
          </Text>
        </View>
      </Card>

      {showReschedule && (
        <View
          style={
            constants.isWeb
              ? styles.buttonWebWrapper
              : styles.buttonMobileWrapper
          }
        >
          <Button
            style={
              constants.isWeb && {
                width: !xs ? 350 : 'auto',
              }
            }
            onPress={onReschedule}
            status="success"
          >
            {() => (
              <Text
                category="c1"
                appearance="alternative"
                style={!constants.isWeb && ThemeStyle.bold}
              >
                {btnTxt}
              </Text>
            )}
          </Button>
        </View>
      )}
    </View>
  );
}

LimitReached.propTypes = {
  maxLimit: PropTypes.number.isRequired,
  productId: PropTypes.string.isRequired,
  // selectorValue: the total value of selector + the existing item (e.g: 2 is display in selector but has 3 on existing cart so the value of this props is 2 + 3 = 5)
  selectorValue: PropTypes.number,
  stocks: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
    categoryName: PropTypes.string,
    isCategoryStock: PropTypes.bool.isRequired,
    remainingStock: PropTypes.number.isRequired,
    stock: PropTypes.number.isRequired,
  }).isRequired,
  storeId: PropTypes.string.isRequired,
  onReschedule: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  mobileWrapper: { ...ThemeStyle.fitWidthContainer },
  webWrapper: { ...ThemeStyle.fitWidthContainer, rowGap: 20 },
  buttonWebWrapper: [
    ThemeStyle.flexDirectionRowCenterSpaceBetween,
    ThemeStyle.alignSelfCenter,
  ],
  buttonMobileWrapper: { ...ThemeStyle.spacingTop },
});

export default LimitReached;
